import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { FiTrendingUp, FiHeart, FiActivity, FiUsers, FiPlay, FiRefreshCw, FiZap } from 'react-icons/fi'
import { 
  Container, 
  Card, 
  Grid, 
  Heading1, 
  Heading2, 
  BodyText, 
  SmallText, 
  Button, 
  StatusBadge,
  LoadingSpinner 
} from '../components/ui'
import { theme, mediaQuery } from '../theme'
import { analyticsAPI, dataHelpers } from '../services/api'

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.xl};
  
  ${mediaQuery.mobile} {
    flex-direction: column;
    gap: ${theme.spacing.md};
  }
`

const HeaderActions = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  
  ${mediaQuery.mobile} {
    width: 100%;
    flex-direction: column;
  }
`

const MetricCard = styled(Card)`
  cursor: pointer;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`

const SuperhumanCard = styled(Card)`
  cursor: pointer;
  transition: transform 0.2s ease;
  background: linear-gradient(135deg, ${theme.colors.status.success}20, ${theme.colors.status.success}10);
  border: 2px solid ${theme.colors.status.success};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px ${theme.colors.status.success}30;
  }
`

const MetricHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.md};
`

const MetricIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: ${theme.borderRadius.md};
  background: ${props => props.color || theme.colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
`

const MetricScore = styled.div`
  font-size: 48px;
  font-weight: 700;
  font-family: ${theme.typography.heading.fontFamily};
  color: ${theme.colors.primary.main};
  line-height: 1;
  margin: ${theme.spacing.md} 0;
`

const MetricDescription = styled.div`
  color: ${theme.colors.primary.light};
  font-size: ${theme.typography.body.p2.fontSize};
  margin-bottom: ${theme.spacing.sm};
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.lg};
`

const StatCard = styled.div`
  text-align: center;
  padding: ${theme.spacing.md};
  background: ${theme.colors.secondary.beige};
  border-radius: ${theme.borderRadius.md};
`

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${theme.colors.primary.main};
  margin-bottom: ${theme.spacing.xs};
`

const StatLabel = styled.div`
  font-size: ${theme.typography.body.p3.fontSize};
  color: ${theme.colors.primary.light};
`

const DashboardPage = () => {
  const [summary, setSummary] = useState(null)
  const [averageScores, setAverageScores] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [error, setError] = useState(null)
  
  useEffect(() => {
    loadDashboardData()
  }, [])
  
  const loadDashboardData = async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      // Load both summary and rankings data
      const [summaryResponse, rankingsResponse] = await Promise.all([
        analyticsAPI.getSummary(),
        analyticsAPI.getRankings()
      ])
      
      setSummary(summaryResponse.data)
      
      // Calculate average scores from rankings data
      const rankings = rankingsResponse.data
      const superhumanCount = calculateSuperhumanCount(rankings.mentalResilienceRankings || [])
      const totalUsersWithResilience = (rankings.mentalResilienceRankings || []).length
      const totalUsersInSystem = summaryResponse.data?.summary?.uniqueUsers || 0
      const superhumanPercentage = totalUsersInSystem > 0 ? Math.round((superhumanCount / totalUsersInSystem) * 100) : 0
      
      const averages = {
        resilience: calculateAverageScore(rankings.mentalResilienceRankings || []),
        superhumanCount: superhumanCount,
        superhumanPercentage: superhumanPercentage,
        totalUsersInSystem: totalUsersInSystem
      }
      setAverageScores(averages)
      
    } catch (err) {
      setError('Failed to load dashboard data. Please check if the backend is running.')
      console.error('Failed to load dashboard data:', err)
    } finally {
      setIsLoading(false)
    }
  }
  
  const calculateAverageScore = (rankings) => {
    if (!rankings || rankings.length === 0) return 0
    const sum = rankings.reduce((total, ranking) => total + ranking.score, 0)
    return sum / rankings.length
  }

  const calculateSuperhumanCount = (rankings) => {
    if (!rankings || rankings.length === 0) return 0
    return rankings.filter(ranking => ranking.score >= 85).length
  }
  
  const loadSummary = loadDashboardData
  
  const handleRunAnalysis = async () => {
    try {
      setIsAnalyzing(true)
      setError(null)
      await analyticsAPI.analyzeAll()
      // Reload dashboard data after analysis
      await loadDashboardData()
    } catch (err) {
      setError('Failed to run analysis. Please try again.')
      console.error('Failed to run analysis:', err)
    } finally {
      setIsAnalyzing(false)
    }
  }
  
  if (isLoading) {
    return (
      <Container>
        <LoadingSpinner />
        <BodyText style={{ textAlign: 'center' }}>Loading dashboard...</BodyText>
      </Container>
    )
  }
  
  if (error) {
    return (
      <Container>
        <Card style={{ textAlign: 'center', background: theme.colors.status.error, color: 'white' }}>
          <Heading2 style={{ color: 'white', marginBottom: theme.spacing.md }}>
            Connection Error
          </Heading2>
          <BodyText style={{ color: 'white', marginBottom: theme.spacing.lg }}>
            {error}
          </BodyText>
          <Button variant="outline" onClick={loadDashboardData} style={{ color: 'white', borderColor: 'white' }}>
            Try Again
          </Button>
        </Card>
      </Container>
    )
  }
  
  const metrics = [
    {
      id: 'resilience',
      title: 'Mental Resilience',
      description: 'How emotions respond to pressure',
      icon: FiTrendingUp,
      color: theme.colors.primary.main,
      link: '/resilience',
      score: averageScores?.resilience || 0,
    },
    {
      id: 'superhumans',
      title: 'Superhumans',
      description: `Users with exceptional resilience (85+ score) - ${averageScores?.superhumanPercentage || 0}% of all users`,
      icon: FiZap,
      color: theme.colors.status.success,
      link: '/rankings',
      score: averageScores?.superhumanCount || 0,
    },
  ]
  
  return (
    <Container>
      <DashboardHeader>
        <div>
          <Heading1>Mental Resilience Dashboard</Heading1>
          <BodyText>
            Analyzing journal entries for mental resilience insights
          </BodyText>
        </div>
        
        <HeaderActions>
          <Button 
            onClick={handleRunAnalysis} 
            disabled={isAnalyzing}
            variant="secondary"
          >
            <FiPlay style={{ marginRight: '8px' }} />
            {isAnalyzing ? 'Running Analysis...' : 'Run Analysis'}
          </Button>
          
          <Button onClick={loadDashboardData} variant="outline">
            <FiRefreshCw style={{ marginRight: '8px' }} />
            Refresh
          </Button>
        </HeaderActions>
      </DashboardHeader>
      
      {/* Summary Stats */}
      {summary && (
        <StatsGrid style={{ marginBottom: theme.spacing.xl }}>
          <StatCard>
            <StatValue>{dataHelpers.formatNumber(summary.connectionInfo?.total_count || 0)}</StatValue>
            <StatLabel>Total Entries</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{dataHelpers.formatNumber(summary.summary?.uniqueUsers || 0)}</StatValue>
            <StatLabel>Users</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{dataHelpers.formatNumber(summary.summary?.totalValidEntries || 0)}</StatValue>
            <StatLabel>Valid Entries</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{summary.summary?.totalValidEntries && summary.connectionInfo?.total_count ? `${Math.round((summary.summary.totalValidEntries / summary.connectionInfo.total_count) * 100)}%` : 'N/A'}</StatValue>
            <StatLabel>Data Quality</StatLabel>
          </StatCard>
        </StatsGrid>
      )}
      
      {/* Metric Cards */}
      <Grid columns="repeat(auto-fit, minmax(300px, 1fr))">
        {metrics.map((metric) => {
          if (metric.id === 'superhumans') {
            // Render superhumans as a count card
            return (
              <Link key={metric.id} to={metric.link} style={{ textDecoration: 'none' }}>
                <SuperhumanCard>
                  <MetricHeader>
                    <div>
                      <Heading2 style={{ fontSize: '20px', marginBottom: '4px' }}>
                        {metric.title}
                      </Heading2>
                      <MetricDescription>{metric.description}</MetricDescription>
                    </div>
                    <MetricIcon color={metric.color}>
                      <metric.icon />
                    </MetricIcon>
                  </MetricHeader>
                  
                  <MetricScore>{metric.score}</MetricScore>
                  
                  <StatusBadge status="excellent" style={{ marginBottom: theme.spacing.md }}>
                    Exceptional users
                  </StatusBadge>
                  
                  <SmallText style={{ marginBottom: theme.spacing.sm, color: theme.colors.primary.light }}>
                    {averageScores?.superhumanCount || 0} out of {averageScores?.totalUsersInSystem || 0} total users
                  </SmallText>
                  
                  <SmallText style={{ marginBottom: '0' }}>
                    Click to view rankings →
                  </SmallText>
                </SuperhumanCard>
              </Link>
            )
          }
          
          // Render other metrics as score cards
          const { score, interpretation } = dataHelpers.formatScore(metric.score, metric.id)
          const status = dataHelpers.getStatusCategory(score)
          
          return (
            <Link key={metric.id} to={metric.link} style={{ textDecoration: 'none' }}>
              <MetricCard>
                <MetricHeader>
                  <div>
                    <Heading2 style={{ fontSize: '20px', marginBottom: '4px' }}>
                      {metric.title}
                    </Heading2>
                    <MetricDescription>{metric.description}</MetricDescription>
                  </div>
                  <MetricIcon color={metric.color}>
                    <metric.icon />
                  </MetricIcon>
                </MetricHeader>
                
                <MetricScore>{score}</MetricScore>
                
                <StatusBadge status={status} style={{ marginBottom: theme.spacing.md }}>
                  {interpretation}
                </StatusBadge>
                
                <SmallText style={{ marginBottom: '0' }}>
                  Click to view detailed analysis →
                </SmallText>
              </MetricCard>
            </Link>
          )
        })}
      </Grid>
      
      {/* Quick Links */}
      <Card style={{ marginTop: theme.spacing.xl }}>
        <Heading2>Quick Actions</Heading2>
        <Grid columns="repeat(auto-fit, minmax(200px, 1fr))">
          <Link to="/rankings" style={{ textDecoration: 'none' }}>
            <Button fullWidth variant="outline">
              <FiUsers style={{ marginRight: '8px' }} />
              View Rankings
            </Button>
          </Link>
          <Link to="/analysis" style={{ textDecoration: 'none' }}>
            <Button fullWidth variant="outline">
              <FiPlay style={{ marginRight: '8px' }} />
              Advanced Analysis
            </Button>
          </Link>
        </Grid>
      </Card>
    </Container>
  )
}

export default DashboardPage 